import { useCallback, useEffect, useMemo, useState, type PropsWithChildren } from 'react'
import type { ActiveRaidModel } from '../types/schema'
import {
  resolveRaid as resolveRaidApi,
  startRaid as startRaidApi,
  subscribeActiveRaid,
  subscribeRaidCurse,
  type StartRaidInput,
} from '../services/raidService'
import { appendRaidHistory } from '../services/raidHistory'
import type { RaidCurseDoc } from '../utils/raidCurse'
import { featureFlag } from '../utils/featureFlags'
import { useWorkspace } from './useWorkspace'
import { RaidContext, type RaidContextValue } from './raidContextCore'

const shallowEqualRaid = (a: ActiveRaidModel | null, b: ActiveRaidModel | null): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.raidId === b.raidId &&
    a.currentBossHp === b.currentBossHp &&
    a.initialBossHp === b.initialBossHp &&
    a.enrageDeadlineTimestamp === b.enrageDeadlineTimestamp &&
    a.bossTitle === b.bossTitle &&
    a.targetElementId === b.targetElementId &&
    a.isPenaltyRaid === b.isPenaltyRaid &&
    a.defaultRaidView === b.defaultRaidView &&
    a.allowedRaidViews.length === b.allowedRaidViews.length &&
    a.allowedRaidViews.every((view, index) => view === b.allowedRaidViews[index]) &&
    a.allocatedCreditPool === b.allocatedCreditPool &&
    Object.keys(a.damageLeaderboard).length === Object.keys(b.damageLeaderboard).length &&
    Object.entries(a.damageLeaderboard).every(([key, entry]) => b.damageLeaderboard[key]?.damageDealt === entry.damageDealt)
  )
}

const shallowEqualCurse = (a: RaidCurseDoc | null, b: RaidCurseDoc | null): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  return a.workspaceId === b.workspaceId && a.expiresAt === b.expiresAt && a.startedAt === b.startedAt
}

export const RaidProvider = ({ children }: PropsWithChildren) => {
  const { activeWorkspaceId, appMode } = useWorkspace()
  const [subscribedRaidState, setSubscribedRaidState] = useState<{ workspaceId: string; raid: ActiveRaidModel | null } | null>(null)
  const [subscribedCurseState, setSubscribedCurseState] = useState<{ workspaceId: string; curse: RaidCurseDoc | null } | null>(null)

  // Personal workspaces historically avoided raid listeners because they
  // returned empty. When the Member personal-workspace feature is enabled,
  // Boss Curse may suppress personal checklist rewards, so subscribe to its
  // workspace signal as well. Firestore remains the access boundary.
  const personalInMemberAppEnabled = featureFlag('personalInMemberApp')
  const raidCapable =
    appMode === 'TEAM' ||
    (personalInMemberAppEnabled && appMode === 'PERSONAL')

  const activeRaid = useMemo<ActiveRaidModel | null>(() => {
    if (!raidCapable || !activeWorkspaceId) return null
    if (subscribedRaidState?.workspaceId === activeWorkspaceId) {
      return subscribedRaidState.raid
    }
    return null
  }, [raidCapable, activeWorkspaceId, subscribedRaidState])

  const isLoading = useMemo(() => {
    if (!raidCapable || !activeWorkspaceId) return false
    return subscribedRaidState?.workspaceId !== activeWorkspaceId
  }, [raidCapable, activeWorkspaceId, subscribedRaidState])

  const activeCurse = useMemo<RaidCurseDoc | null>(() => {
    if (!raidCapable || !activeWorkspaceId) return null
    if (subscribedCurseState?.workspaceId === activeWorkspaceId) {
      return subscribedCurseState.curse
    }
    return null
  }, [raidCapable, activeWorkspaceId, subscribedCurseState])

  useEffect(() => {
    if (!raidCapable || !activeWorkspaceId) return undefined
    const currentWorkspaceId = activeWorkspaceId
    return subscribeActiveRaid(currentWorkspaceId, (raid) => {
      setSubscribedRaidState((current) => {
        if (current?.workspaceId === currentWorkspaceId && shallowEqualRaid(current.raid, raid)) {
          return current
        }
        return { workspaceId: currentWorkspaceId, raid }
      })
    })
  }, [raidCapable, activeWorkspaceId])

  useEffect(() => {
    if (!raidCapable || !activeWorkspaceId) return undefined
    const currentWorkspaceId = activeWorkspaceId
    return subscribeRaidCurse(currentWorkspaceId, (curse) => {
      setSubscribedCurseState((current) => {
        if (current?.workspaceId === currentWorkspaceId && shallowEqualCurse(current.curse, curse)) {
          return current
        }
        return { workspaceId: currentWorkspaceId, curse }
      })
    })
  }, [raidCapable, activeWorkspaceId])

  const startRaid = useCallback(
    (input: StartRaidInput) => startRaidApi(input),
    [],
  )

  const resolveRaid = useCallback(
    async (isVictory: boolean) => {
      if (!activeWorkspaceId) throw new Error('No active workspace.')
      const snapshot = activeRaid
      const result = await resolveRaidApi(activeWorkspaceId, isVictory)
      // Client-side history is capped to 10 per workspace. Server-side raid
      // history is Phase 2 scope — see agents/architecture.md.
      if (snapshot) appendRaidHistory(activeWorkspaceId, snapshot, isVictory ? 'VICTORY' : 'FAILURE')
      return result
    },
    [activeWorkspaceId, activeRaid],
  )

  const value = useMemo<RaidContextValue>(
    () => ({ activeRaid, activeCurse, isLoading, startRaid, resolveRaid }),
    [activeRaid, activeCurse, isLoading, startRaid, resolveRaid],
  )

  return <RaidContext.Provider value={value}>{children}</RaidContext.Provider>
}
